/**
 * A date window: a few presets, and two date boxes for anything else.
 *
 * Built for the dashboard (Parinaam, 9 Oct 2026). The presets are there because
 * almost every real question is one of them — "this month", "this quarter",
 * "this year" — and making somebody type two dates to ask the question they ask
 * weekly is the kind of friction that stops a filter being used at all. The two
 * boxes are there because the question that is not on the list is the one
 * somebody actually needed a picker for.
 *
 * **Both ends are independent and either may be empty.** A `from` alone means
 * "since", a `to` alone means "up to", and neither means all time. That is the
 * API's reading too, so the control cannot express a window the server would
 * answer differently.
 *
 * The two boxes bound each other with `min`/`max`, so a backwards range cannot
 * be entered here at all. The API rejects one anyway — a saved link or a script
 * can still send one — but the browser should not let somebody build a question
 * that has no answer.
 */

import { useId, useState } from 'react';
import styles from './DateRangePicker.module.css';

export interface DateRange {
  /** `YYYY-MM-DD`, or '' for no bound. */
  from: string;
  to: string;
}

export interface DateRangePickerProps {
  value: DateRange;
  onChange: (value: DateRange) => void;
  /** Describes the group for screen readers, e.g. "Dashboard period". */
  label?: string;
}

/** `YYYY-MM-DD` in local time — never `toISOString`, which shifts by timezone. */
function isoDay(date: Date): string {
  const year = date.getFullYear();
  const month = `${date.getMonth() + 1}`.padStart(2, '0');
  const day = `${date.getDate()}`.padStart(2, '0');
  return `${year}-${month}-${day}`;
}

/**
 * The presets, computed when the control renders rather than at module load.
 *
 * A tab left open overnight would otherwise offer yesterday's "this month" —
 * which is exactly the kind of quietly wrong figure a dashboard must not show.
 */
function presets(): { label: string; range: DateRange }[] {
  const today = new Date();
  const start = (year: number, month: number, day: number) => isoDay(new Date(year, month, day));

  const year = today.getFullYear();
  const month = today.getMonth();
  const quarterStart = Math.floor(month / 3) * 3;

  const daysAgo = (count: number) => {
    const date = new Date(today);
    date.setDate(date.getDate() - count);
    return isoDay(date);
  };

  return [
    // All time first, because it is the default and the way back.
    { label: 'All time', range: { from: '', to: '' } },
    { label: 'Last 30 days', range: { from: daysAgo(30), to: isoDay(today) } },
    { label: 'This month', range: { from: start(year, month, 1), to: isoDay(today) } },
    { label: 'This quarter', range: { from: start(year, quarterStart, 1), to: isoDay(today) } },
    { label: 'This year', range: { from: start(year, 0, 1), to: isoDay(today) } },
    /*
     * Last financial year, April to March — the Indian one, which is the year
     * Parinaam reports against. A calendar "last year" would be the wrong
     * twelve months for every funder conversation this organisation has.
     */
    {
      label: 'Last FY (Apr–Mar)',
      range:
        month >= 3
          ? { from: start(year - 1, 3, 1), to: start(year, 2, 31) }
          : { from: start(year - 2, 3, 1), to: start(year - 1, 2, 31) },
    },
  ];
}

export function DateRangePicker({ value, onChange, label = 'Period' }: DateRangePickerProps) {
  const fromId = useId();
  const toId = useId();
  const options = presets();

  /*
   * Which chip was pressed, where the window alone cannot say.
   *
   * Two presets can describe the same window: in the first month of a quarter
   * "this month" and "this quarter" are the same ten days, and on 1 January so
   * is "this year". Picking the active chip by matching the range alone then
   * lights whichever is listed first — so pressing "This quarter" on 10 October
   * filtered correctly and highlighted "This month", which reads as a button
   * that does not work.
   */
  const [pressed, setPressed] = useState<string | null>(null);

  const matching = options.filter(
    (option) => option.range.from === value.from && option.range.to === value.to
  );

  /*
   * The pressed one wins among equals; otherwise the first match. That fallback
   * is what keeps the highlight right when the window did not come from a chip
   * at all — a parent's saved state, or the two date boxes below.
   */
  const active =
    matching.find((option) => option.label === pressed) ?? matching[0] ?? undefined;

  return (
    <div className={styles.picker} role="group" aria-label={label}>
      <div className={styles.presets}>
        {options.map((option) => (
          <button
            key={option.label}
            type="button"
            className={`${styles.preset} ${active?.label === option.label ? styles.presetOn : ''}`}
            // A pressed state rather than a selected one: these are buttons that
            // set the two boxes below, and the boxes are the actual value.
            aria-pressed={active?.label === option.label}
            onClick={() => {
              setPressed(option.label);
              onChange(option.range);
            }}
          >
            {option.label}
          </button>
        ))}
      </div>

      <div className={styles.custom}>
        <label className={styles.dateLabel} htmlFor={fromId}>
          From
        </label>
        <input
          id={fromId}
          type="date"
          className={styles.date}
          value={value.from}
          // Cannot be set past the other end — the window would have no answer.
          max={value.to || undefined}
          onChange={(event) => {
            setPressed(null);
            onChange({ ...value, from: event.target.value });
          }}
        />

        <label className={styles.dateLabel} htmlFor={toId}>
          To
        </label>
        <input
          id={toId}
          type="date"
          className={styles.date}
          value={value.to}
          min={value.from || undefined}
          onChange={(event) => {
            setPressed(null);
            onChange({ ...value, to: event.target.value });
          }}
        />

        {(value.from || value.to) && (
          <button
            type="button"
            className={styles.reset}
            onClick={() => {
              setPressed(null);
              onChange({ from: '', to: '' });
            }}
          >
            Reset
          </button>
        )}
      </div>
    </div>
  );
}
